import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../utils/reportsApi';
import './ReportCommon.css';

function fmtTs(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'medium', hour12: false });
}

const PAGE_SIZES = [25, 50, 100];

const AuditLogPage = () => {
  const navigate = useNavigate();

  const [userId,   setUserId]   = useState('');
  const [module,   setModule]   = useState('');
  const [action,   setAction]   = useState('');
  const [status,   setStatus]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [limit,    setLimit]    = useState(50);
  const [page,     setPage]     = useState(1);

  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const buildParams = (p) => ({
    userId:   userId   || undefined,
    module:   module   || undefined,
    action:   action   || undefined,
    status:   status   || undefined,
    dateFrom: dateFrom || undefined,
    dateTo:   dateTo   || undefined,
    page: p,
    limit,
  });

  const run = async (p = 1) => {
    setLoading(true); setError(null);
    try {
      const data = await reportsApi.getAuditLog(buildParams(p));
      setResult(data);
      setPage(p);
    } catch (e) {
      setError(e.message || 'Failed to run report');
    } finally { setLoading(false); }
  };

  const items      = result?.items ?? [];
  const totalPages = result ? Math.ceil(result.total / limit) : 0;

  return (
    <div className="rpt-page">
      <div className="rpt-header">
        <button className="rpt-back" onClick={() => navigate('/reports')}><i className="fas fa-arrow-left"></i> Reports</button>
        <h1 className="rpt-title">User Actions Log</h1>
      </div>

      <div className="rpt-filters">
        <label className="rpt-label">
          User ID
          <input type="text" value={userId} onChange={(e) => setUserId(e.target.value)}
            placeholder="paste UUID…" className="rpt-input" style={{ minWidth: 220 }} />
        </label>
        <label className="rpt-label">
          Module
          <input type="text" value={module} onChange={(e) => setModule(e.target.value)}
            placeholder="e.g. vouchers" className="rpt-input" style={{ minWidth: 130 }} />
        </label>
        <label className="rpt-label">
          Action contains
          <input type="text" value={action} onChange={(e) => setAction(e.target.value)}
            placeholder="e.g. login" className="rpt-input" style={{ minWidth: 130 }} />
        </label>
        <label className="rpt-label">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rpt-select" style={{ minWidth: 110 }}>
            <option value="">All</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="FAILURE">FAILURE</option>
          </select>
        </label>
        <label className="rpt-label">
          From
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rpt-input" />
        </label>
        <label className="rpt-label">
          To
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rpt-input" />
        </label>
        <label className="rpt-label">
          Per page
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="rpt-select" style={{ minWidth: 80 }}>
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="rpt-filter-actions">
          <button className="rpt-btn rpt-btn--primary" onClick={() => run(1)} disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Running…</> : <><i className="fas fa-play"></i> Run</>}
          </button>
        </div>
      </div>

      {error && <div className="rpt-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {result && (
        <div className="rpt-result">
          <div className="rpt-meta">
            {result.total.toLocaleString()} event{result.total !== 1 ? 's' : ''}
            &nbsp;·&nbsp; page {page} of {totalPages || 1}
          </div>
          <div className="rpt-table-wrap">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Module</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Entity Type</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="7" className="rpt-empty">No log entries found.</td></tr>
                )}
                {items.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {fmtTs(l.timestamp)}
                    </td>
                    <td>{l.user?.fullName || l.user?.username || '—'}</td>
                    <td><code style={{ fontSize: '0.78rem' }}>{l.module}</code></td>
                    <td><code style={{ fontSize: '0.78rem' }}>{l.action}</code></td>
                    <td>
                      <span className={`al-chip al-chip--${l.status?.toLowerCase()}`}>{l.status}</span>
                    </td>
                    <td>{l.entityType || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.78rem' }}>{l.ipAddress || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="rpt-pagination">
              <button className="rpt-btn rpt-btn--primary" onClick={() => run(page - 1)} disabled={page <= 1 || loading}>
                <i className="fas fa-chevron-left"></i> Prev
              </button>
              <span className="rpt-pagination__info">Page {page} / {totalPages}</span>
              <button className="rpt-btn rpt-btn--primary" onClick={() => run(page + 1)} disabled={page >= totalPages || loading}>
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        .al-chip { display:inline-block; padding:1px 8px; border-radius:999px; font-size:0.75rem; font-weight:600; }
        .al-chip--success { background:#dcfce7; color:#166534; }
        .al-chip--failure { background:#fee2e2; color:#991b1b; }
        .rpt-pagination {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          padding: var(--spacing-md) var(--spacing-lg);
          border-top: 1px solid var(--border-subtle);
        }
        .rpt-pagination__info { font-size: var(--font-size-sm); color: var(--text-secondary); }
      `}</style>
    </div>
  );
};

export default AuditLogPage;
